// Location: src/components/NavigationBar.js
import React, { useState, useRef, useEffect } from 'react';
import navbarCircle from '../assets/navbar_logo_circle.png';
import navbarText from '../assets/bvm_navbar_logo.png';
import '../styles/tailwind.css';
import { Link, useLocation } from 'react-router-dom';

const NavigationBar = () => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [industriesOpen, setIndustriesOpen] = useState(false);
    const [areasOpen, setAreasOpen] = useState(false);
    const industriesRef = useRef(null);
    const areasRef = useRef(null);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (industriesRef.current && !industriesRef.current.contains(e.target)) setIndustriesOpen(false);
            if (areasRef.current && !areasRef.current.contains(e.target)) setAreasOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsOpen(false);
        setIndustriesOpen(false);
        setAreasOpen(false);
    }, [location.pathname]);

    const isActive = (path) => location.pathname === path;
    const isActiveGroup = (prefix) => location.pathname.startsWith(prefix);

    const navLinkClass = (path) =>
        `relative px-3 py-2 text-base font-medium transition-colors duration-200 ${
            isActive(path)
                ? 'text-white border-b-2 border-white'
                : 'text-white/80 hover:text-white hover:border-b-2 hover:border-white/50'
        }`;

    const dropdownLinkClass = (path) =>
        `block px-4 py-2 text-sm transition-colors duration-200 ${
            isActive(path) ? 'text-white bg-green-900' : 'text-white/80 hover:text-white hover:bg-green-900/50'
        }`;

    const DropdownMenu = ({ label, isOpenState, setOpenState, closeOther, ref, prefix, items }) => (
        <div className="relative" ref={ref}>
            <button
                onClick={() => { if (!isOpenState) closeOther(); setOpenState(!isOpenState); }}
                className={`relative px-3 py-2 text-base font-medium transition-colors duration-200 flex items-center gap-1 ${
                    isActiveGroup(prefix)
                        ? 'text-white border-b-2 border-white'
                        : 'text-white/80 hover:text-white hover:border-b-2 hover:border-white/50'
                }`}
            >
                {label}
                <svg className={`w-3.5 h-3.5 transition-transform ${isOpenState ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpenState && (
                <div className="absolute top-full left-0 mt-1 w-48 rounded-md shadow-lg bg-green-700 border border-green-600/50 overflow-hidden" style={{ zIndex: 10000 }}>
                    {items.map(({ to, label: itemLabel }) => (
                        <Link key={to} to={to} className={dropdownLinkClass(to)}>{itemLabel}</Link>
                    ))}
                </div>
            )}
        </div>
    );

    const industryItems = [
        { to: '/industries/railroad', label: 'Railroad' },
        { to: '/industries/oil-gas', label: 'Oil & Gas' },
        { to: '/industries/municipal', label: 'Municipal' },
        { to: '/industries/utility', label: 'Utility' },
    ];

    const areaItems = [
        { to: '/service-areas/lower-mainland', label: 'Lower Mainland' },
        { to: '/service-areas/okanagan', label: 'Okanagan' },
        { to: '/service-areas/interior-bc', label: 'Interior' },
        { to: '/service-areas/kootenays', label: 'Kootenays' },
        { to: '/service-areas/northern-bc', label: 'Northern' },
    ];

    return (
        <nav className="fixed top-0 w-full bg-green-900 shadow-md text-white border-b border-white/10" style={{ minHeight: '3.5rem', zIndex: 9999 }}>
            <div className="max-w-6xl mx-auto px-4 flex justify-between items-center" style={{ minHeight: '3.5rem' }}>
                {/* Logo — always visible */}
                <Link to="/" className="flex items-center shrink-0 gap-2">
                    <img src={navbarCircle} alt="" width={500} height={500} className="navbar-logo-circle" />
                    <img src={navbarText} alt="Boultbee Vegetation Management" width={560} height={194} className="navbar-logo-text" />
                </Link>

                {/* Desktop nav */}
                <div className="hidden md:flex items-center space-x-1">
                    <Link to="/services" className={navLinkClass('/services')}>Services</Link>
                    <Link to="/processes" className={navLinkClass('/processes')}>Processes</Link>
                    <DropdownMenu
                        label="Industries"
                        isOpenState={industriesOpen}
                        setOpenState={setIndustriesOpen}
                        closeOther={() => setAreasOpen(false)}
                        ref={industriesRef}
                        prefix="/industries"
                        items={industryItems}
                    />
                    <DropdownMenu
                        label="Service Areas"
                        isOpenState={areasOpen}
                        setOpenState={setAreasOpen}
                        closeOther={() => setIndustriesOpen(false)}
                        ref={areasRef}
                        prefix="/service-areas"
                        items={areaItems}
                    />
                    <Link to="/about-us" className={navLinkClass('/about-us')}>About Us</Link>
                    <Link to="/faq" className={navLinkClass('/faq')}>FAQ</Link>
                    <Link to="/contact" className={navLinkClass('/contact')}>Contact</Link>
                </div>

                {/* Hamburger button */}
                <div className="md:hidden flex items-center">
                    <button onClick={() => setIsOpen(!isOpen)} className="text-white p-2" aria-label="Toggle navigation menu">
                        {isOpen ? (
                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        ) : (
                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {isOpen && (
                <div className="md:hidden bg-green-900 border-t border-green-700/50" style={{ maxHeight: 'calc(100dvh - 3.5rem)', overflowY: 'auto' }}>
                    <div className="px-4 py-3 space-y-1">
                        <Link to="/services" className={`block py-3.5 px-3 rounded text-base font-medium min-h-[44px] flex items-center ${isActive('/services') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>Services</Link>
                        <Link to="/processes" className={`block py-3.5 px-3 rounded text-base font-medium min-h-[44px] flex items-center ${isActive('/processes') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>Processes</Link>

                        {/* Industries expandable */}
                        <div>
                            <button
                                onClick={() => { if (!industriesOpen) setAreasOpen(false); setIndustriesOpen(!industriesOpen); }}
                                className={`w-full flex justify-between items-center py-3.5 px-3 rounded text-base font-medium min-h-[44px] ${isActiveGroup('/industries') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}
                            >
                                Industries
                                <svg className={`w-4 h-4 transition-transform ${industriesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {industriesOpen && (
                                <div className="pl-6 space-y-1 mt-1">
                                    {industryItems.map(({ to, label }) => (
                                        <Link key={to} to={to} className={`block py-2.5 px-3 rounded text-sm min-h-[40px] flex items-center ${isActive(to) ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>{label}</Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Service Areas expandable */}
                        <div>
                            <button
                                onClick={() => { if (!areasOpen) setIndustriesOpen(false); setAreasOpen(!areasOpen); }}
                                className={`w-full flex justify-between items-center py-3.5 px-3 rounded text-base font-medium min-h-[44px] ${isActiveGroup('/service-areas') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}
                            >
                                Service Areas
                                <svg className={`w-4 h-4 transition-transform ${areasOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {areasOpen && (
                                <div className="pl-6 space-y-1 mt-1">
                                    {areaItems.map(({ to, label }) => (
                                        <Link key={to} to={to} className={`block py-2.5 px-3 rounded text-sm min-h-[40px] flex items-center ${isActive(to) ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>{label}</Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Link to="/about-us" className={`block py-3.5 px-3 rounded text-base font-medium min-h-[44px] flex items-center ${isActive('/about-us') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>About Us</Link>
                        <Link to="/faq" className={`block py-3.5 px-3 rounded text-base font-medium min-h-[44px] flex items-center ${isActive('/faq') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>FAQ</Link>
                        <Link to="/contact" className={`block py-3.5 px-3 rounded text-base font-medium min-h-[44px] flex items-center ${isActive('/contact') ? 'bg-green-700 text-white' : 'text-gray-300 hover:bg-green-800 hover:text-white'}`}>Contact</Link>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default NavigationBar;
